<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { decryptWithEmojiPin, decodeBase58 } from '@tokenbox/wallet-kit'
import { useWalletSweeper } from '../../composables/useWalletSweeper'
import { getNetworkConfig, getMinDepositAmount } from '../../config/currencies'
import { apiConfig } from '../../config/api'
import { useNotifications } from '../../composables/useNotifications'
import GiftCardBalanceView from '../wallet/GiftCardBalanceView.vue'
import CryptoDepositWidget from '../wallet/CryptoDepositWidget.vue'
import WalletMoonPayWidget from '../wallet/WalletMoonPayWidget.vue'
import WalletWithdrawal from '../wallet/WalletWithdrawal.vue'
import WalletStatusScreen from '../wallet/WalletStatusScreen.vue'

const { t: $t } = useI18n()

const props = defineProps({
  selectedCurrency: {
    type: Object,
    required: true
  },
  wallet: {
    type: Object,
    required: true
  },
  initialBgStyle: {
    type: Number,
    default: 0
  },
  initialMessage: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['wallet-unlocked', 'wallet-locked', 'reset-wallet', 'balance-updated'])

const { showSuccess, showError } = useNotifications()

// Card state
const bgStyle = ref(props.initialBgStyle)
const message = ref(props.initialMessage)

// Wallet state
const mnemonic = ref('')
const currentEmojiPin = ref('')
const walletChain = ref('base_sepolia')
const isRefreshing = ref(false)
const internalStep = ref('balance') // balance | crypto-deposit | moonpay | destination | transferring | completed | error

// Show action buttons in balance mode
const showActionButtons = computed(() => internalStep.value === 'balance')

// Get default network from currency
const defaultNetworkId = computed(() => {
  const networks = props.selectedCurrency?.networks
  return networks && networks.length > 0 ? networks[0].networkId : 'base_sepolia'
})

// Network configuration
const resolvedNetwork = computed(() => getNetworkConfig(walletChain.value))
const fallbackNetwork = computed(() => getNetworkConfig(defaultNetworkId.value))

const rpcUrl = computed(() => {
  return resolvedNetwork.value?.rpcUrl
    || fallbackNetwork.value?.rpcUrl
    || 'https://ethereum-sepolia-rpc.publicnode.com'
})

const explorerBaseUrl = computed(() => {
  return resolvedNetwork.value?.explorerUrl
    || fallbackNetwork.value?.explorerUrl
    || null
})

// Initialize wallet sweeper
const sweeper = useWalletSweeper({
  rpcUrl: rpcUrl.value,
  chain: walletChain.value,
  currency: props.selectedCurrency,
  paymasterUrl: apiConfig.paymasterUrl,
})

const {
  wallet,
  destination: destinationRef,
  transferResult,
  error: sweeperError,
  loading,
  canProceed,
  setDestination,
  importWallet,
  fetchBalance,
  executeTransfer,
  executeGaslessTransfer,
  reset,
  updateConfig,
} = sweeper

// Computed properties
const walletAddress = computed(() => wallet.value?.address ?? '')
const balanceDisplay = computed(() => {
  if (!wallet.value?.balance) return '0.0000'
  const numeric = Number.parseFloat(wallet.value.balance)
  return Number.isFinite(numeric) ? numeric.toFixed(4) : wallet.value.balance
})

const isGaslessSupported = computed(() => {
  if (props.selectedCurrency?.type !== 'erc20') return false
  const supportedChains = new Set(['base_sepolia'])
  return supportedChains.has(walletChain.value)
})

const sweeperErrorMessage = computed(
  () => sweeperError.value?.message ?? $t('claimView.error.defaultMessage')
)

const txUrl = computed(() => {
  const hash = transferResult.value?.txHash
  if (!hash) return null
  const baseUrl = explorerBaseUrl.value
  if (!baseUrl) return null
  const sanitizedBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
  return `${sanitizedBase}/tx/${hash}`
})

const isWithdrawReady = computed(() => canProceed.value && !!wallet.value && !loading.value)

// Watch for config changes
watch(
  [walletChain, rpcUrl, () => props.selectedCurrency],
  ([chain, url, currency]) => {
    updateConfig({
      chain,
      rpcUrl: url,
      currency: currency,
    })
  },
  { immediate: true }
)

// Auto-unlock wallet on mount
const autoUnlockWallet = async () => {
  if (!props.wallet?.claimUrl || !props.wallet?.emojiPin) return

  try {
    const hashIndex = props.wallet.claimUrl.indexOf('#')
    if (hashIndex === -1) return

    const hash = props.wallet.claimUrl.slice(hashIndex + 1)
    const encodedPayload = decodeBase58(hash)

    const decryptedPayload = await decryptWithEmojiPin(encodedPayload, props.wallet.emojiPin)

    mnemonic.value = decryptedPayload.mnemonic
    currentEmojiPin.value = props.wallet.emojiPin
    walletChain.value = decryptedPayload.metadata.chain || defaultNetworkId.value

    console.log('🔓 [TopUpAndShare] Wallet auto-unlocked')

    // Update sweeper config
    updateConfig({
      chain: walletChain.value,
      rpcUrl: rpcUrl.value,
      currency: props.selectedCurrency,
    })

    // Import wallet
    await importWallet(decryptedPayload.mnemonic)
    internalStep.value = 'balance'

    // Notify parent
    emit('wallet-unlocked', { ...decryptedPayload, emojiPin: props.wallet.emojiPin })
  } catch (err) {
    console.error('❌ [TopUpAndShare] Failed to auto-unlock wallet:', err)
    showError('Failed to unlock wallet')
  }
}

// Initialize on mount
autoUnlockWallet()

// Balance refresh
const refreshBalance = async () => {
  isRefreshing.value = true
  try {
    await fetchBalance()
  } catch (err) {
    console.error('Failed to refresh balance:', err)
  } finally {
    setTimeout(() => {
      isRefreshing.value = false
    }, 600)
  }
}

// Watch balance changes and emit to parent
watch(balanceDisplay, (newBalance) => {
  emit('balance-updated', newBalance)
}, { immediate: true })

// Expose public methods
defineExpose({
  refreshBalance
})

// Deposit method selection
const handleDepositMethodSelected = (method) => {
  if (loading.value) return
  console.log('💳 [TopUpAndShare] Deposit method selected:', method)

  // Proceed directly to deposit
  if (method === 'crypto') {
    // Show unified crypto deposit widget
    internalStep.value = 'crypto-deposit'
  } else if (method === 'card') {
    // Show MoonPay widget for fiat purchases
    internalStep.value = 'moonpay'
  }
}

// Back from crypto deposit widget
const handleBackFromCryptoDeposit = () => {
  console.log('⬅️ [TopUpAndShare] Back from crypto deposit')
  goBackToBalance()
}

// Withdrawal
const startWithdrawal = () => {
  if (!isWithdrawReady.value) return
  internalStep.value = 'destination'
  destinationRef.value = ''
}

const goBackToBalance = () => {
  internalStep.value = 'balance'
  destinationRef.value = ''
}

// Transaction handlers
// CryptoDepositWidget handles transactions and monitoring internally

const handleDepositReceived = async (balance) => {
  console.log('✅ [TopUpAndShare] Deposit received:', balance)
  showSuccess(`Deposit received: ${balance.balanceEth} ${props.selectedCurrency?.code || 'ETH'}`)

  // Refresh balance and return to balance view
  await refreshBalance()
  goBackToBalance()
}

// Withdrawal handling
const handleWithdrawalSubmit = async ({ destination }) => {
  if (loading.value) return

  const transferType = isGaslessSupported.value ? 'gasless' : 'regular'
  console.log(`🚀 [TopUpAndShare] Starting ${transferType} transfer...`)

  try {
    setDestination(destination)
    destinationRef.value = destination

    internalStep.value = 'transferring'

    // Execute the appropriate transfer method
    if (isGaslessSupported.value) {
      await executeGaslessTransfer()
    } else {
      await executeTransfer()
    }

    internalStep.value = 'completed'
    console.log('✅ [TopUpAndShare] Transfer completed')
  } catch (err) {
    console.error(`❌ [TopUpAndShare] Transfer failed:`, err)
    internalStep.value = 'error'
  }
}

// Return to balance after transfer
const returnToBalance = async () => {
  loading.value = true
  try {
    // Re-import wallet if it was wiped after transfer
    if (!wallet.value && mnemonic.value) {
      console.log('🔄 [TopUpAndShare] Re-importing wallet to check balance...')
      await importWallet(mnemonic.value)
    } else {
      // Just refresh balance if wallet still exists
      await refreshBalance()
    }
    internalStep.value = 'balance'
  } catch (err) {
    console.error('Failed to return to balance view:', err)
    internalStep.value = 'balance'
  } finally {
    loading.value = false
  }
}

// Error handling
const retryImport = async () => {
  if (!mnemonic.value) return
  reset()
  internalStep.value = 'balance'
  destinationRef.value = ''
  try {
    await importWallet(mnemonic.value)
  } catch (err) {
    console.error('Retry import failed:', err)
    internalStep.value = 'error'
  }
}
</script>

<template>
  <div class="flex flex-col gap-6 w-full animate-slide-up">
    <!-- Content Below Card - Changes Based on Internal Step -->
    <div class="flex flex-col gap-6">
      <!-- Balance Mode: Deposit/Share Buttons -->
      <GiftCardBalanceView
        v-if="showActionButtons"
        :wallet="props.wallet"
        :loading="loading"
        @deposit-method-selected="handleDepositMethodSelected"
        @withdraw-requested="startWithdrawal"
      />

      <!-- Crypto Deposit Widget (Unified: Direct + Exchange + Monitoring) -->
      <CryptoDepositWidget
        v-if="internalStep === 'crypto-deposit'"
        :wallet-address="walletAddress"
        :wallet-currency="selectedCurrency"
        :wallet-network="resolvedNetwork || fallbackNetwork"
        @back="handleBackFromCryptoDeposit"
        @deposit-received="handleDepositReceived"
      />

      <!-- MoonPay Widget -->
      <WalletMoonPayWidget
        v-if="internalStep === 'moonpay'"
        :address="walletAddress"
        :currency="selectedCurrency?.code || 'ETH'"
        :currency-info="selectedCurrency"
        @back="goBackToBalance"
      />

      <!-- Withdrawal Form -->
      <WalletWithdrawal
        v-if="internalStep === 'destination'"
        :balance="balanceDisplay"
        :currency="selectedCurrency?.code || 'ETH'"
        :is-gasless="isGaslessSupported"
        :loading="loading"
        @submit="handleWithdrawalSubmit"
        @cancel="goBackToBalance"
      />

      <!-- Transferring Status -->
      <WalletStatusScreen
        v-else-if="internalStep === 'transferring'"
        status="loading"
        :title="$t('claimView.transferring.title')"
        :message="$t('claimView.transferring.message')"
      />

      <!-- Completed Status -->
      <WalletStatusScreen
        v-else-if="internalStep === 'completed'"
        status="success"
        :title="$t('claimView.completed.title')"
      >
        <template #details>
          <div class="flex flex-col gap-3 p-4 bg-vault-bg-secondary rounded-lg">
            <div class="flex justify-between items-center text-sm">
              <span class="text-vault-text-secondary">{{ $t('claimView.completed.amount') }}</span>
              <strong class="text-vault-text-primary font-semibold">{{ transferResult?.amount }} {{ selectedCurrency?.code || 'ETH' }}</strong>
            </div>
            <div class="flex justify-between items-center text-sm">
              <span class="text-vault-text-secondary">{{ $t('claimView.completed.to') }}</span>
              <code class="text-vault-text-primary font-semibold font-mono text-xs break-all">{{ transferResult?.to }}</code>
            </div>
            <div v-if="txUrl" class="flex justify-between items-center text-sm">
              <span class="text-vault-text-secondary">{{ $t('claimView.completed.transaction') }}</span>
              <a :href="txUrl" target="_blank" class="text-vault-purple-400 no-underline font-semibold hover:underline">{{ $t('claimView.completed.viewOnExplorer') }}</a>
            </div>
          </div>
        </template>

        <template #actions>
          <button type="button" class="btn-primary" @click="returnToBalance">
            {{ $t('claimView.backToBalance') }}
          </button>
        </template>
      </WalletStatusScreen>

      <!-- Error Status -->
      <WalletStatusScreen
        v-else-if="internalStep === 'error'"
        status="error"
        :title="$t('claimView.error.title')"
        :message="sweeperErrorMessage"
      >
        <template #actions>
          <button type="button" class="btn-primary" :disabled="loading" @click="returnToBalance">
            {{ $t('claimView.backToBalance') }}
          </button>
          <button type="button" class="btn-ghost" :disabled="loading" @click="retryImport">
            {{ $t('claimView.error.retry') }}
          </button>
        </template>
      </WalletStatusScreen>
    </div>
  </div>
</template>

<style scoped>
/* Styles removed - using BaseModal component in parent */
</style>
